import { Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core';
import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useDataService from '../../../hooks/useDataService';
import { eMessageType } from '../../../types/IMessageType';
import { IOfferTimeRange } from '../../../types/IItemSetup';
import { getDateFormat } from '../../../_lib/lib';
import { PrimaryButton, TertiaryButton } from '../../AtomComponents';
import LifecycleDatesRow from './LifecycleDatesRow';
import OfferDatesRow from './OfferDatesRow';
import ShippingDatesRow from './ShippingDatesRow';
import {
  buildCreateDatesPayload,
  commonDateFormat,
  getAdjustedShippingStartDate,
  getCreateDatesRangeError,
  getDaysBetween,
  getInitialCreateDatesState,
  getLifecycleDates,
  getShippingStartDisabledBefore,
  IFilterOptions,
  ILifecycleDates,
  IShowNoDate,
} from './createDatesUtils';
import './DateRangeSelector.scss';

interface IDateRangeSelector {
  customOptionObj: any;
  clearFilter: any;
  saveFilter: any;
  closeDialog: any;
  applyButtonLabel?: any;
  clearButtonLabel?: any;
  disableStartBefore?: string;
  disableBothDates?: {
    endDate?: { after?: string };
  };
  setLoadingDialog: any;
  overLappingShippingDates: any;
  daysToFinalize: number;
  offeringTimeRange: IOfferTimeRange;
}

function DateRangeSelector({
  customOptionObj,
  saveFilter,
  closeDialog,
  applyButtonLabel = 'SAVE',
  clearButtonLabel = 'CANCEL',
  setLoadingDialog,
  overLappingShippingDates,
  daysToFinalize,
  offeringTimeRange,
}: IDateRangeSelector) {
  // initial filter set
  const [filterOptions, setFilterOptions] = useState<IFilterOptions>({
    offerDates: null,
    shippingDates: null,
  });
  const [lifecycleDates, setLifecycleDates] = useState<ILifecycleDates>({
    negotiatingDates: {},
    finalizingDates: {},
  });
  const [showNoDate, setShowNoDate] = useState<IShowNoDate>({
    offerStart: true,
    offerEnd: true,
    shippingStart: true,
    shippingEnd: true,
  });
  const [disabledDates, setDisabledDates] = useState<any>({
    offerDates: {
      before: moment().format(commonDateFormat),
      after: moment().format(commonDateFormat),
    },
    shippingDates: {},
  });
  const offerDatesRowRef = useRef<any>();
  const shippingDatesRowRef = useRef<any>();
  const { openSnackBar } = useDataService();

  useEffect(() => {
    if (!customOptionObj) return;
    try {
      const initialState = getInitialCreateDatesState(customOptionObj);
      // set disabled dates
      if (initialState.disabledDates) setDisabledDates(initialState.disabledDates);
      // set rangeDates
      setFilterOptions(initialState.filterOptions);
    } catch (error: any) {
      console.error(error);
      setFilterOptions({ ...filterOptions });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLifecycleDates(
      getLifecycleDates({ filterOptions, showNoDate, offeringTimeRange, daysToFinalize })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterOptions]);

  const setOfferStartDate = (val: any) => {
    let endDate: any;
    const newStartDate = moment(val, commonDateFormat);
    if (showNoDate.offerStart) setShowNoDate({ ...showNoDate, offerStart: false });
    if (
      filterOptions?.offerDates?.endDate &&
      moment(filterOptions?.offerDates?.endDate, commonDateFormat).isBefore(newStartDate)
    ) {
      endDate = newStartDate.add(1, 'day').format(commonDateFormat);
    }
    setFilterOptions({
      ...filterOptions,
      offerDates: { startDate: val, endDate: endDate ?? filterOptions?.offerDates?.endDate },
    });
  };

  const setOfferEndDate = (val: any) => {
    if (showNoDate.offerEnd) setShowNoDate({ ...showNoDate, offerEnd: false });
    const adjustedShippingStartDate = getAdjustedShippingStartDate({
      offerEndDate: val,
      shippingStartDate: filterOptions?.shippingDates?.startDate,
      daysToFinalize,
    });
    setFilterOptions({
      ...filterOptions,
      offerDates: { startDate: filterOptions?.offerDates?.startDate || '', endDate: val },
      shippingDates: {
        startDate: adjustedShippingStartDate ?? filterOptions?.shippingDates?.startDate ?? '',
        endDate: filterOptions?.shippingDates?.endDate || '',
      },
    });
    if (!getDateFormat(val)) return;
    setDisabledDates({
      offerDates: { ...disabledDates.offerDates, after: val },
      shippingDates: {
        ...disabledDates.shippingDates,
        before: getShippingStartDisabledBefore({
          offerEndDate: val,
          adjustedShippingStartDate,
          shippingDisabledBefore: disabledDates.shippingDates.before,
          overLappingShippingDates,
          daysToFinalize,
        }),
      },
    });
  };

  const setShippingStartDate = (val: any) => {
    let endDate: any;
    const newStartDate = moment(val, commonDateFormat);
    if (showNoDate.shippingStart) setShowNoDate({ ...showNoDate, shippingStart: false });
    if (
      filterOptions?.shippingDates?.endDate &&
      moment(filterOptions?.shippingDates?.endDate, commonDateFormat).isSameOrBefore(newStartDate)
    ) {
      endDate = newStartDate.add(1, 'day').format(commonDateFormat);
    }
    setFilterOptions({
      ...filterOptions,
      shippingDates: { startDate: val, endDate: endDate ?? filterOptions?.shippingDates?.endDate },
    });
  };

  const setShippingEndDate = (val: any) => {
    setFilterOptions({
      ...filterOptions,
      shippingDates: {
        startDate: filterOptions?.shippingDates?.startDate || '',
        endDate: val,
      },
    });
    if (showNoDate.shippingEnd) setShowNoDate({ ...showNoDate, shippingEnd: false });
  };

  const isRangeIncomplete = Object.keys(showNoDate).some(
    (item: string) => showNoDate[item as keyof IShowNoDate]
  );

  const dateRangeError = useMemo(() => {
    if (isRangeIncomplete) return '';
    return getCreateDatesRangeError({
      offerDates: filterOptions.offerDates,
      shippingDates: filterOptions.shippingDates,
      daysToFinalize,
    });
  }, [isRangeIncomplete, filterOptions, daysToFinalize]);

  const showInvalidDatesError = () => {
    // show error
    offerDatesRowRef?.current?.showError(`Invalid Dates`);
    shippingDatesRowRef?.current?.showError(`Invalid Dates`);
  };

  const handleApply = async () => {
    if (isRangeIncomplete || dateRangeError) return;
    const updatedFilter = buildCreateDatesPayload({
      offerDates: filterOptions?.offerDates,
      shippingDates: filterOptions?.shippingDates,
      lifecycleDates,
    });
    if (!updatedFilter) return showInvalidDatesError();

    // handle filter
    setLoadingDialog(true);
    const res = await saveFilter(updatedFilter);
    if (res) closeDialog();
    else openSnackBar('Could Not Make Changes', eMessageType.error);
    setLoadingDialog(false);
  };

  const handleClear = () => {
    closeDialog();
  };

  const negotiatingDays = lifecycleDates.negotiatingDates?.endDate
    ? getDaysBetween(filterOptions.offerDates?.endDate, lifecycleDates.negotiatingDates.endDate)
    : '--';
  const finalizingDays = lifecycleDates.finalizingDates?.endDate
    ? getDaysBetween(
        lifecycleDates.finalizingDates.startDate,
        lifecycleDates.finalizingDates.endDate
      )
    : '--';

  return (
    <Dialog
      onClose={() => closeDialog()}
      className="px-create-dates-dialog"
      open={!!customOptionObj}>
      <DialogTitle>
        <span className="px-chips-filter-title">Create New Dates</span>
      </DialogTitle>

      <DialogContent>
        <div className="grid-x">
          <div className="margin-bottom-2 text-faded" style={{ fontSize: '12px' }}>
            Finalizing days is currently set to{' '}
            {daysToFinalize === 1 ? `${daysToFinalize} Day` : `${daysToFinalize} Days`}. You must
            leave a {1 + daysToFinalize}-day gap minimum between the Offer end date and Shipping
            start date for Negotiating and Finalizing
          </div>
          <OfferDatesRow
            ref={offerDatesRowRef}
            filterOptions={filterOptions}
            showNoDate={showNoDate}
            disabledDates={disabledDates}
            offeringTimeRange={offeringTimeRange}
            daysToFinalize={daysToFinalize}
            setOfferStartDate={setOfferStartDate}
            setOfferEndDate={setOfferEndDate}
          />
          <LifecycleDatesRow
            label="Negotiating"
            days={negotiatingDays}
            startDate={lifecycleDates.negotiatingDates?.startDate}
            endDate={lifecycleDates.negotiatingDates?.endDate}
          />
          <LifecycleDatesRow
            label="Finalizing"
            days={finalizingDays}
            startDate={lifecycleDates.finalizingDates?.startDate}
            endDate={lifecycleDates.finalizingDates?.endDate}
            className="margin-top-2"
          />
          <ShippingDatesRow
            ref={shippingDatesRowRef}
            filterOptions={filterOptions}
            showNoDate={showNoDate}
            daysToFinalize={daysToFinalize}
            setShippingStartDate={setShippingStartDate}
            setShippingEndDate={setShippingEndDate}
          />
          {!!dateRangeError && (
            <div className="cell small-12 margin-top-2 px-create-dates-range-error">
              {dateRangeError}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="px-chips-filter-dialog-actions">
        <TertiaryButton onClick={handleClear}>{clearButtonLabel}</TertiaryButton>
        <PrimaryButton onClick={handleApply} disabled={isRangeIncomplete || !!dateRangeError}>
          {applyButtonLabel}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export default DateRangeSelector;
